// useContentEditable.ts
import { useCallback, useEffect, useRef, useState } from 'react'
import { DOMUtils } from '../../Utils/domUtils'

interface UseContentEditableProps {
  maxLength: number
  isMessageSecond?: boolean
  onChange: (value: string) => void
  initialValue: string
  domainOptions: string[]
  isEmail?: boolean
}

interface UseContentEditableReturn {
  handleInput: () => void
  handlePaste: (event: any) => void
  handleKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void
  handleBackspace: (e: React.KeyboardEvent<HTMLDivElement>) => void
  insertPlaceholder: (placeholder: string) => void
  cleanEditableContent: () => void
  serializeContent: () => string
  updateContentEditable: (value: string) => void
  getCaretPosition: () => number
  getRawText: () => string
  contentEditableRef: React.RefObject<HTMLDivElement>
}

export const useContentEditable = ({
  maxLength,
  onChange,
  initialValue,
  domainOptions,
}: UseContentEditableProps): UseContentEditableReturn => {
  const contentEditableRef = useRef<HTMLDivElement>(null)
  const [rawText, setRawText] = useState(initialValue)
  const lastValueRef = useRef(initialValue)
  const selectionRangeRef = useRef<Range | null>(null)

  const isFirstMount = useRef(true)

  const saveSelection = useCallback(() => {
    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      selectionRangeRef.current = selection.getRangeAt(0)
    }
  }, [])

  const restoreSelection = useCallback(() => {
    const selection = window.getSelection()
    if (selection && selectionRangeRef.current) {
      selection.removeAllRanges()
      selection.addRange(selectionRangeRef.current)
    }
  }, [])

  const serializeContent = useCallback(() => {
    return DOMUtils.serializeContent(contentEditableRef.current!)
  }, [])

  const handleInput = useCallback(() => {
    saveSelection()
    const newRawText = serializeContent()

    if (newRawText.length <= maxLength) {
      setRawText(newRawText) // Update state here
      lastValueRef.current = newRawText
      onChange(newRawText)
    }

    restoreSelection()
  }, [maxLength, onChange, restoreSelection, saveSelection, serializeContent])

  const getRawText = useCallback(() => rawText, [rawText])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      DOMUtils.handleEnterKey(e.nativeEvent)
      saveSelection()
    },
    [saveSelection]
  )

  const handleBackspace = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      DOMUtils.handleBackspace(e.nativeEvent)
      saveSelection()
    },
    [saveSelection]
  )

  const insertPlaceholder = useCallback((placeholder: string) => {
    const contentEditable = contentEditableRef.current;
    if (!contentEditable) return;

    // Focus explicitly
    contentEditable.focus();

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    let range = selection.getRangeAt(0);
    
    // Step 1: If inside text node, split it
    if (range.startContainer.nodeType === Node.TEXT_NODE) {
      const textNode = range.startContainer as Text;
      const text = textNode.nodeValue || '';
      const before = text.slice(0, range.startOffset);
      const after = text.slice(range.startOffset);

      const beforeNode = document.createTextNode(before);
      const afterNode = document.createTextNode(after);

      const parent = textNode.parentNode!;
      parent.replaceChild(afterNode, textNode);
      parent.insertBefore(beforeNode, afterNode);

      // Update range
      range = document.createRange();
      range.setStartAfter(beforeNode);
      range.collapse(true);
    }

    // Step 2: Create badge and space
    const badge = DOMUtils.createBadgeElement({ placeholder });
    const space = document.createTextNode('\u00A0');

    // Step 3: Use fragment to insert cleanly
    const frag = document.createDocumentFragment();
    frag.appendChild(badge);
    frag.appendChild(space);

    range.insertNode(frag);

    // Step 4: Move caret after space
    const afterRange = document.createRange();
    afterRange.setStartAfter(space);
    afterRange.collapse(true);

    selection.removeAllRanges();
    selection.addRange(afterRange);

    // Optional: trigger re-parse/render
    handleInput();

  }, [handleInput]);

  const createBadgeCountMap = (elements: HTMLElement[]): Map<string | null, number> => {
    const map = new Map<string | null, number>()
    for (const el of elements) {
      const key = el.textContent
      const currentCount = map.get(key) || 0
      map.set(key, currentCount + 1)
    }
    return map
  }

  const setCursorPosition = (node: Node, offset?: number): void => {
    const selection = window.getSelection()
    if (!selection) return

    const range = document.createRange()

    // Check if a specific offset inside the node is requested
    if (offset !== undefined) {
      range.setStart(node, offset)
    } else {
      // Default to the original behavior: place cursor after the node
      range.setStartAfter(node)
    }

    range.collapse(true) // Make it a cursor, not a selection
    selection.removeAllRanges()
    selection.addRange(range)
  }

  // Modify updateContentEditable
  const updateContentEditable = useCallback(
    (value: string) => {
      const contentEditable = contentEditableRef.current
      if (!contentEditable) return

      // Force update on first mount even if value matches
      if (isFirstMount.current || value !== lastValueRef.current) {
        const existingBadges = Array.from(contentEditable.querySelectorAll<HTMLElement>('.badge'))
        const existingBadgeCounts = createBadgeCountMap(existingBadges)

        let parsedHtml = value

        if (parsedHtml) {
          // URL detection and replacement
          parsedHtml = parsedHtml.replace(
            new RegExp(`(${domainOptions.join('|')})\\/\\w+`, 'g'),
            (match) => DOMUtils.createBadgeElement({ url: match }).outerHTML
          )

          // Placeholder detection and replacement
          parsedHtml = parsedHtml.replace(
            /\{\{\[(\w+(?:\s\w+)*)\]\}\}/g,
            (_, p1) => DOMUtils.createBadgeElement({ placeholder: p1 }).outerHTML
          )
        }

        contentEditable.innerHTML = parsedHtml || ''
        // 4. Get the new list of badges and count them.
        const newBadges = Array.from(contentEditable.querySelectorAll<HTMLElement>('.badge'))
        const newBadgeCounts = createBadgeCountMap(newBadges)

        // 5. Find which badge key was added by comparing the counts.
        let addedBadgeKey: string | null = null
        let found = false // Flag to allow breaking the loop

        newBadgeCounts.forEach((newCount, key) => {
          if (found) return // a forEach loop can't be broken, so we just skip the rest

          const oldCount = existingBadgeCounts.get(key) || 0

          if (newCount > oldCount) {
            addedBadgeKey = key
            found = true // Set flag to stop processing
          }
        })

        // 6. Find the last physical instance of that badge and set the cursor.
        if (addedBadgeKey) {
          const candidateBadges = newBadges.filter((b) => b.textContent === addedBadgeKey)
          const targetBadge = candidateBadges[candidateBadges.length - 1]

          if (targetBadge) {
            const nextNode = targetBadge?.nextSibling

            if (nextNode) {
              // move cursor to last badge and place it after space
              setCursorPosition(nextNode, 1)
            }
          }
        }

        lastValueRef.current = value
        setRawText(value)
        DOMUtils.cleanEditableContent(contentEditable)
        isFirstMount.current = false
      }
    },
    [domainOptions]
  )

  const cleanEditableContent = useCallback(() => {
    DOMUtils.cleanEditableContent(contentEditableRef.current)
  }, [])

  const getCaretPosition = useCallback(() => {
    return DOMUtils.getCaretPosition(contentEditableRef.current!)
  }, [])

  const handlePaste = useCallback((event) => {
    event.preventDefault()

    const text = event.clipboardData.getData('text/plain')
    
    const selection = window.getSelection()
    if (!selection?.rangeCount) return // Exit if there's no selection.

    const range = selection.getRangeAt(0)
    range.deleteContents()

    const textNode = document.createTextNode(text)
    range.insertNode(textNode)

     const newRawText = serializeContent()
    if (newRawText.length <= maxLength) {
      setRawText(newRawText) // Update state here
      lastValueRef.current = newRawText
      onChange(newRawText)
    }


    selection.collapseToEnd()

  }, [onChange, maxLength, serializeContent])

  // Initialize content
  useEffect(() => {
    updateContentEditable(initialValue)
  }, [initialValue, updateContentEditable])

  return {
    handleInput,
    handleKeyDown,
    handleBackspace,
    insertPlaceholder,
    cleanEditableContent,
    serializeContent,
    updateContentEditable,
    getCaretPosition,
    contentEditableRef,
    getRawText,
    handlePaste,
  }
}
